import { useEffect, useState } from 'react';
import { NODE_TYPE_DATABASE, EDGE_TYPE_CONTAINS } from './theme';
import { docProcessingApi, DocumentInfo } from '../../services/docProcessingApi';
import { getAuthHeaders } from '../../services/apiClient';

export interface SchemaColumn {
  name: string;
  dataType: string;
  length: string;
  description: string;
}

export interface SchemaNode {
  id: string;
  type: string;
  name: string;
  description?: string;
  database?: string;
  schema?: string;
  columnCount?: number;
  tableCount?: number;
  primaryKey?: string[];
  columns?: SchemaColumn[];
  /** Primary (first) module — kept for compatibility; prefer `modules`. */
  module?: string;
  moduleId?: string;
  /** ALL modules this table belongs to (many-to-many, e.g. Accounting). */
  modules?: string[];
}

export interface SchemaEdge {
  source: string;
  target: string;
  type: string;
  sourceColumn?: string;
  targetColumn?: string;
  /** Indirect relationship (shared-table path, CR impact graph). */
  indirect?: boolean;
  description?: string;
}

export interface SchemaGraph {
  nodes: SchemaNode[];
  edges: SchemaEdge[];
}

function isRelationshipEdge(edge: SchemaEdge): boolean {
  return edge.type !== EDGE_TYPE_CONTAINS && edge.type !== 'module_contains';
}

// Build adjacency maps for lineage traversal
export function buildAdjacency(edges: SchemaEdge[]) {
  const predecessors = new Map<string, Set<string>>();
  const successors = new Map<string, Set<string>>();

  for (const edge of edges) {
    if (!isRelationshipEdge(edge)) continue;

    if (!successors.has(edge.source)) successors.set(edge.source, new Set());
    successors.get(edge.source)!.add(edge.target);

    if (!predecessors.has(edge.target)) predecessors.set(edge.target, new Set());
    predecessors.get(edge.target)!.add(edge.source);
  }

  return { predecessors, successors };
}

// Walk full lineage (upstream or downstream)
export function walkLineage(
  startId: string,
  adjacency: Map<string, Set<string>>,
  maxDepth = 10
): Set<string> {
  const visited = new Set<string>();
  const queue: { id: string; depth: number }[] = [{ id: startId, depth: 0 }];
  let head = 0;

  while (head < queue.length) {
    const { id, depth } = queue[head++];
    if (visited.has(id) || depth > maxDepth) continue;
    visited.add(id);
    const neighbors = adjacency.get(id);
    if (neighbors) {
      neighbors.forEach((n) => {
        if (!visited.has(n)) queue.push({ id: n, depth: depth + 1 });
      });
    }
  }

  visited.delete(startId);
  return visited;
}

// Extract databases and schemas from the graph
export function extractMetadata(graph: SchemaGraph): { databases: string[]; schemas: string[] } {
  const databases = new Set<string>();
  const schemas = new Set<string>();

  for (const n of graph.nodes) {
    if (n.type === NODE_TYPE_DATABASE) databases.add(n.name);
    if (n.schema) schemas.add(`${n.database}.${n.schema}`);
  }

  return {
    databases: Array.from(databases).sort(),
    schemas: Array.from(schemas).sort(),
  };
}

// ── Reusable loaders + hook ───────────────────────────────────────────────────
//
// Both the standalone Graph Viz page and the embedded SchemaBrowserModal need
// the same schema graph + documents list. Cache at module level (30-min TTL)
// so opening the modal after Graph Viz has already loaded doesn't re-fetch.
// SchemaGraphPage maintains its own separate, richer cache for backwards
// compatibility — the two caches are independent, and a fresh-load can warm
// either one first.

const _CACHE_TTL_MS = 30 * 60 * 1000;
let _graphCache: { data: SchemaGraph; ts: number } | null = null;
let _docsCache: { data: DocumentInfo[]; ts: number } | null = null;
let _moduleGraphCache: Map<string, { data: SchemaGraph; ts: number }> = new Map();

/** Fetch the schema graph, hitting the API first and falling back to the
 *  static `/schema_graph.json` bundle if the API is unreachable. */
export async function loadSchemaGraph(): Promise<SchemaGraph> {
  const now = Date.now();
  if (_graphCache && now - _graphCache.ts < _CACHE_TTL_MS) return _graphCache.data;
  try {
    const r = await fetch('/api/v1/schema/graph', { headers: await getAuthHeaders() });
    if (!r.ok) throw new Error(`API ${r.status}`);
    const data = (await r.json()) as SchemaGraph;
    _graphCache = { data, ts: Date.now() };
    return data;
  } catch {
    // API unreachable — fall back to the static bundle. The page-level
    // loader logs this same scenario explicitly; we stay quieter here so
    // the modal doesn't double-log when both consumers are mounted.
    const r = await fetch('/schema_graph.json');
    if (!r.ok) throw new Error(`Fallback fetch failed: HTTP ${r.status}`);
    const data = (await r.json()) as SchemaGraph;
    _graphCache = { data, ts: Date.now() };
    return data;
  }
}

/** Fetch the documents list used to attribute tables to documents. */
export async function loadSchemaDocuments(): Promise<DocumentInfo[]> {
  const now = Date.now();
  if (_docsCache && now - _docsCache.ts < _CACHE_TTL_MS) return _docsCache.data;
  const result = await docProcessingApi.listDocuments();
  const docs = result.documents || [];
  _docsCache = { data: docs, ts: Date.now() };
  return docs;
}

/** React hook returning the cached schema graph + documents. Re-uses the
 *  module-level cache; only fetches if the cache is empty or stale. */
export function useSchemaGraphData(): {
  graphData: SchemaGraph | null;
  documents: DocumentInfo[];
  loading: boolean;
  error: string | null;
} {
  const [graphData, setGraphData] = useState<SchemaGraph | null>(
    _graphCache && Date.now() - _graphCache.ts < _CACHE_TTL_MS ? _graphCache.data : null,
  );
  const [documents, setDocuments] = useState<DocumentInfo[]>(
    _docsCache && Date.now() - _docsCache.ts < _CACHE_TTL_MS ? _docsCache.data : [],
  );
  const [loading, setLoading] = useState(!graphData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadSchemaGraph(), loadSchemaDocuments()])
      .then(([g, d]) => {
        if (cancelled) return;
        setGraphData(g);
        setDocuments(d);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return { graphData, documents, loading, error };
}

/** Fetch a filtered schema graph for a specific module. */
export async function loadFilteredSchemaGraph(moduleName: string): Promise<SchemaGraph> {
  const now = Date.now();
  const cached = _moduleGraphCache.get(moduleName);
  if (cached && now - cached.ts < _CACHE_TTL_MS) return cached.data;

  try {
    const r = await fetch(`/api/v1/schema/graph?module=${encodeURIComponent(moduleName)}`, {
      headers: await getAuthHeaders(),
    });
    if (!r.ok) throw new Error(`API ${r.status}`);
    const data = (await r.json()) as SchemaGraph;
    _moduleGraphCache.set(moduleName, { data, ts: Date.now() });
    return data;
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err));
  }
}

/** React hook for filtered schema graph data for a specific module. */
export function useFilteredSchemaGraphData(moduleName: string | null): {
  graphData: SchemaGraph | null;
  documents: DocumentInfo[];
  loading: boolean;
  error: string | null;
} {
  const [graphData, setGraphData] = useState<SchemaGraph | null>(null);
  const [documents, setDocuments] = useState<DocumentInfo[]>(
    _docsCache && Date.now() - _docsCache.ts < _CACHE_TTL_MS ? _docsCache.data : [],
  );
  const [loading, setLoading] = useState(moduleName !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!moduleName) {
      setGraphData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    Promise.all([
      loadFilteredSchemaGraph(moduleName),
      loadSchemaDocuments(),
    ])
      .then(([g, d]) => {
        if (cancelled) return;
        setGraphData(g);
        setDocuments(d);
        setLoading(false);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [moduleName]);

  return { graphData, documents, loading, error };
}
